import { useState, useEffect, useRef } from "react";

import { Icon } from "../icons/Icon";

interface DropdownItem {
    id: number | string;
    name: string;
}

interface DropdownProps {
    id?: string;
    name?: string;
    items: DropdownItem[];
    selectedItems: DropdownItem[];
    isMultiSelect?: boolean;
    onSelect: (item: DropdownItem, selected: boolean) => void;
    onClear?: () => void;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    isValid?: boolean;
    className?: string;
    additionalProps?: Partial<React.InputHTMLAttributes<HTMLInputElement>>;
}

export const Dropdown = ({
    id,
    name,
    items,
    selectedItems,
    isMultiSelect = false,
    onSelect,
    onClear,
    placeholder,
    disabled,
    required,
    isValid = true,
    className,
    additionalProps,
}: DropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setTimeout(() => {
                    setIsOpen(false);
                }, 200);
            }
        };
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    const isItemSelected = (item: DropdownItem) => {
        return selectedItems.some(
            (selectedItem) => selectedItem.id === item.id
        );
    };

    const handleSelect = (item?: DropdownItem) => {
        if (!isDirty) {
            setIsDirty(true);
        }
        if (item) {
            onSelect(item, !isItemSelected(item));
        } else {
            onClear?.();
        }
        if (!isMultiSelect) {
            setIsOpen(false);
        }
    };

    const isInvalid =
        isDirty && (!isValid || (required && !selectedItems.length));

    return (
        <div className="dropdown-container w-full" ref={dropdownRef}>
            <div className="relative w-full" onClick={() => setIsOpen(!isOpen)}>
                <input
                    id={id}
                    name={name}
                    type="text"
                    autoComplete="off"
                    required={required}
                    placeholder={placeholder}
                    value={selectedItems.map((item) => item.name).join(", ")}
                    readOnly={true}
                    className={`${selectedItems.length ? "filled" : ""} ${
                        isInvalid ? "invalid" : "valid"
                    } ${className} cursor-pointer`}
                    {...additionalProps}
                />
                {!disabled && (
                    <div className="input-select-icon">
                        <Icon
                            name={isOpen ? "toggle-up" : "toggle-down"}
                            width={16}
                            height={17}
                        />
                    </div>
                )}
            </div>
            {isOpen && (
                <div className="dropdown">
                    <div className="dropdown-inner">
                        {!isMultiSelect && !required && onClear && (
                            <div
                                className="dropdown-item placeholder"
                                onClick={() => handleSelect(undefined)}
                            >
                                {placeholder || `נקה בחירה`}
                            </div>
                        )}

                        {items.map((item) => (
                            <div
                                key={item.id}
                                onClick={() => handleSelect(item)}
                                className="dropdown-item"
                            >
                                {isMultiSelect && (
                                    <Icon
                                        name={
                                            isItemSelected(item)
                                                ? "checked"
                                                : "circle"
                                        }
                                        width={15}
                                        height={15}
                                    />
                                )}
                                {item.name}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
